// The one implementation of live product search (header SearchBox and the
// command palette): debounced `GET /products?search=` for in-stock titles,
// newest request wins. Also owns the device's recent-search list, written
// when a search is submitted. Rendering and keyboard handling stay with the
// caller.
import { computed, onScopeDispose, ref, watch } from 'vue';
import { api } from '../api/client';

export interface ProductSuggestion {
  id: string;
  name: string;
  platform: string | null;
  ourPriceCents: number;
  coverUrl: string | null;
  imageUrl: string | null;
}

const DEBOUNCE_MS = 220;
export const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 6;

/** `onResults` fires after each successful fetch (SearchBox opens its list). */
export function useProductSearch(onResults?: () => void) {
  const query = ref('');
  const trimmed = computed(() => query.value.trim());
  const results = ref<ProductSuggestion[]>([]);
  const loading = ref(false);
  let timer: number | undefined;
  let requestId = 0;

  async function fetchSuggestions() {
    const q = trimmed.value;
    const id = ++requestId;
    loading.value = true;
    try {
      const { data } = await api.get<{ items: ProductSuggestion[] }>('/products', {
        params: { search: q, limit: SEARCH_LIMIT, inStockOnly: true },
      });
      if (id !== requestId) return; // a newer query is in flight
      results.value = data.items;
      onResults?.();
    } catch {
      if (id === requestId) results.value = [];
    } finally {
      if (id === requestId) loading.value = false;
    }
  }

  watch(trimmed, (q) => {
    window.clearTimeout(timer);
    if (q.length < SEARCH_MIN_CHARS) {
      requestId++; // drop any response still in flight
      results.value = [];
      loading.value = false;
      return;
    }
    loading.value = true; // covers the debounce gap, so callers never flash "nothing found"
    timer = window.setTimeout(fetchSuggestions, DEBOUNCE_MS);
  });

  onScopeDispose(() => window.clearTimeout(timer));

  return { query, trimmed, results, loading };
}

// ── Recent searches (per device, newest first) ──────────────────────────
const RECENT_SEARCHES_KEY = 'gamzor.searches.v1';
const RECENT_SEARCHES_MAX = 6;

function readRecent(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string').slice(0, RECENT_SEARCHES_MAX) : [];
  } catch {
    return [];
  }
}

// Module-level so every caller sees the same list without a store.
const recentSearches = ref<string[]>(readRecent());

function writeRecent(list: string[]) {
  recentSearches.value = list;
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
  } catch {
    // private mode / quota: the list just lives in memory
  }
}

export function useRecentSearches() {
  function remember(q: string) {
    const term = q.trim();
    if (term.length < SEARCH_MIN_CHARS) return;
    const rest = recentSearches.value.filter((s) => s.toLowerCase() !== term.toLowerCase());
    writeRecent([term, ...rest].slice(0, RECENT_SEARCHES_MAX));
  }
  function forget(q: string) {
    writeRecent(recentSearches.value.filter((s) => s !== q));
  }
  return { recentSearches, remember, forget };
}
